import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MapContainer, GeoJSON, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  Loader2, MapPin, Users, Clock, Truck, Palmtree, Map as MapIcon,
} from "lucide-react";
import { toast } from "sonner";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
               "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

// Normaliza nombre de provincia para comparar (sin "provincia de", sin tildes, lowercase)
const normProv = (s) => {
  if (!s) return "";
  return String(s)
    .toLowerCase()
    .replace(/^provincia de /i, "")
    .replace(/^provincia /i, "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

// Escala color: blanco (sin datos) → ámbar oscuro (intenso)
const colorForValue = (value, max) => {
  if (!max || value === 0) return "#27272a"; // zinc-800
  const t = Math.min(1, value / max);
  // gradient zinc → ámbar
  const r = Math.round(39 + (245 - 39) * t);
  const g = Math.round(39 + (158 - 39) * t);
  const b = Math.round(42 + (11 - 42) * t);
  return `rgb(${r},${g},${b})`;
};

export default function CargaTrabajo() {
  const [data, setData] = useState(null);
  const [geo, setGeo] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/rrhh/carga-trabajo", { params: { year, month } });
      setData(d);
    } catch {
      toast.error("Error cargando carga de trabajo");
    } finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  // Cargar GeoJSON solo una vez
  useEffect(() => {
    fetch("/data/spain-provinces.geojson")
      .then((r) => r.json())
      .then(setGeo)
      .catch(() => toast.error("Error cargando mapa"));
  }, []);

  // Map provincia → trabajos
  const provMap = useMemo(() => {
    const m = new Map();
    (data?.por_provincia || []).forEach((p) => m.set(normProv(p.provincia), p));
    return m;
  }, [data]);

  const maxTrabajos = useMemo(() => {
    return Math.max(1, ...(data?.por_provincia || []).map((p) => p.trabajos));
  }, [data]);

  const styleFeature = (feature) => {
    const key = normProv(feature.properties.name);
    const p = provMap.get(key);
    return {
      fillColor: colorForValue(p?.trabajos || 0, maxTrabajos),
      weight: 0.6,
      opacity: 1,
      color: "#3f3f46",
      fillOpacity: 0.85,
    };
  };

  const onEachFeature = (feature, layer) => {
    const key = normProv(feature.properties.name);
    const p = provMap.get(key);
    const trabajos = p?.trabajos || 0;
    const empleados = p?.empleados || 0;
    layer.bindTooltip(
      `<div style="font-size:11px"><b>${feature.properties.name}</b><br/>${trabajos} trabajos · ${empleados} empleado(s)</div>`,
      { sticky: true, className: "leaflet-tooltip-dark" }
    );
  };

  return (
    <div className="p-4 lg:p-6" data-testid="carga-trabajo-page">
      <PageHeader
        title="Carga de trabajo del equipo"
        subtitle="Cruce rutas + horas fichaje + ausencias por empleado y provincia"
      />

      {/* Filtros */}
      <div className="bg-zinc-900/40 border border-zinc-800/70 rounded-lg p-3 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs text-zinc-400">Año</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-28" data-testid="ct-year"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              {[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Mes</Label>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-40" data-testid="ct-month"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <SelectItem value="0">Todo el año</SelectItem>
              {MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando…
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="ct-kpis">
            <KPI icon={Users} label="Empleados" value={data.totales.empleados} />
            <KPI icon={Truck} label="Rutas" value={data.totales.rutas} color="amber" />
            <KPI icon={MapPin} label="Trabajos" value={data.totales.trabajos} color="emerald" />
            <KPI icon={Clock} label="Horas fichaje" value={`${data.totales.horas_trabajadas} h`} color="sky" />
            <KPI icon={Palmtree} label="Días ausencia" value={data.totales.dias_ausencia} color="red" />
            <KPI icon={MapIcon} label="Provincias" value={data.totales.provincias} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Heatmap España */}
            <div className="lg:col-span-3 bg-zinc-900/30 border border-zinc-800/60 rounded-lg p-2 overflow-hidden" data-testid="ct-mapa">
              <div className="px-2 pt-1 pb-2 text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                <MapIcon className="w-3.5 h-3.5" /> Mapa de calor por provincia
              </div>
              <div style={{ height: "520px", width: "100%" }} className="rounded overflow-hidden">
                <MapContainer
                  center={[40.0, -3.7]}
                  zoom={5}
                  scrollWheelZoom={false}
                  style={{ height: "100%", width: "100%", background: "#09090b" }}
                  zoomControl={true}
                  attributionControl={false}
                >
                  {geo && (
                    <GeoJSON
                      data={geo}
                      style={styleFeature}
                      onEachFeature={onEachFeature}
                      key={maxTrabajos}  // re-render al cambiar datos
                    />
                  )}
                </MapContainer>
              </div>
              {/* Leyenda */}
              <div className="px-2 mt-2 flex items-center gap-2 text-[10px] text-zinc-500">
                <span>0</span>
                <div className="flex-1 h-2 rounded" style={{ background: "linear-gradient(to right, #27272a, #f59e0b)" }}></div>
                <span>{maxTrabajos} trabajos</span>
              </div>
            </div>

            {/* Top provincias */}
            <div className="lg:col-span-2 bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden" data-testid="ct-provincias">
              <div className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                Top provincias ({data.por_provincia.length})
              </div>
              {data.por_provincia.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">Sin actividad en el periodo</div>
              ) : (
                <div className="divide-y divide-zinc-800/60 max-h-[480px] overflow-y-auto">
                  {data.por_provincia.map((p) => {
                    const pct = (p.trabajos / maxTrabajos) * 100;
                    return (
                      <div key={p.provincia} className="px-3 py-2" data-testid={`prov-${normProv(p.provincia)}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-sm text-zinc-200 truncate">{p.provincia.replace(/^provincia de /i, "")}</span>
                          <span className="text-xs font-mono text-amber-300">{p.trabajos} <span className="text-zinc-500">trab.</span></span>
                        </div>
                        <div className="h-1.5 bg-zinc-800 rounded overflow-hidden">
                          <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">{p.empleados} empleado(s)</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Tabla empleados */}
          <div className="mt-6 bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden" data-testid="ct-empleados">
            <div className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              Carga por empleado
            </div>
            <table className="w-full text-sm">
              <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <tr>
                  <th className="text-left p-2.5">Empleado</th>
                  <th className="text-right p-2.5">Rutas</th>
                  <th className="text-right p-2.5">Trabajos</th>
                  <th className="text-right p-2.5">Horas fichaje</th>
                  <th className="text-right p-2.5">Días ausencia</th>
                  <th className="text-left p-2.5">Provincias trabajadas</th>
                </tr>
              </thead>
              <tbody>
                {data.empleados.map((e) => (
                  <tr key={e.user_id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40"
                    data-testid={`emp-${e.user_id}`}>
                    <td className="p-2.5 text-zinc-100">{e.user_nombre}</td>
                    <td className="p-2.5 text-right text-zinc-300 font-mono">{e.rutas}</td>
                    <td className="p-2.5 text-right text-emerald-300 font-mono font-semibold">{e.trabajos}</td>
                    <td className="p-2.5 text-right text-sky-300 font-mono">{e.horas_trabajadas.toFixed(1)} h</td>
                    <td className={`p-2.5 text-right font-mono ${e.dias_ausencia > 0 ? "text-red-300" : "text-zinc-500"}`}>
                      {e.dias_ausencia}
                    </td>
                    <td className="p-2.5">
                      <div className="flex flex-wrap gap-1">
                        {e.provincias.slice(0, 4).map((p) => (
                          <span key={p.provincia} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">
                            {p.provincia.replace(/^provincia de /i, "")} · {p.trabajos}
                          </span>
                        ))}
                        {e.provincias.length > 4 && (
                          <span className="text-[10px] text-zinc-500">+{e.provincias.length - 4}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  const txt = {
    amber: "text-amber-300",
    emerald: "text-emerald-300",
    sky: "text-sky-300",
    red: "text-red-300",
  }[color] || "text-zinc-100";
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`text-xl font-semibold mt-1 ${txt}`}>{value}</div>
    </div>
  );
}
